import { useEffect, useState } from 'react';
import { useCan, useOrganizationId, useSubjectLanguageVariants } from '@features/user';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { isBatchRunning, isBatchStopped } from '../bulkVocabulary';
import { requirementKindLabel } from '../constants';
import {
  useGetBatchesQuery,
  useGetBatchPreviewQuery,
  useGetBatchQuery,
  useGetRequestCopyPreviewQuery,
} from '../store/api';
import { toBatchProgressData, toBulkLaunchPreviewData } from '../store/bulkLaunchMapping';
import { useBatchCancel } from '../store/useBatchCancel';
import { useBulkLaunch } from '../store/useBulkLaunch';

import { BatchProgressPanel } from './BatchProgressPanel';
import { BulkLaunchConfirmDialog } from './BulkLaunchConfirmDialog';
import { BulkLaunchPreview } from './BulkLaunchPreview';

/** How often a running batch re-reads its progress. */
const PROGRESS_POLL_MS = 5000;

export interface BulkLaunchRowProps {
  definitionId: string;
  requirementLabel: string;
  requirementKind?: string;
  /**
   * The definition's panel evaluation is still running (GDN-3590).
   *
   * Not a display flag: the preview is skipped, not hidden. Previewing during a
   * run makes the request repeat the background task's own Stripe walk over the
   * whole active panel, which is what times out and produces the "could not be
   * loaded" alert staff were seeing — and every visit to the settings page
   * during a run started another walk.
   */
  isMaterializing?: boolean;
}

/**
 * Bulk launch from a requirement's catalog row (AC-10 … AC-14).
 *
 * **The preview is practice-admin only, and that is not a display rule.**
 * Verifying the population settles requirement rows org-wide and attributes
 * the read to the caller, so it is a write wearing a GET. A non-admin must
 * never CALL it — hiding the result afterwards would still have mutated state
 * on their behalf — which is why the query is skipped rather than the panel
 * hidden, and why the control that opens it is withheld rather than disabled.
 *
 * The flow opens as a modal from the requirement's expanded catalog row
 * (GDN-4142, the human's call on the GDN-4123 round): the preview, the confirm
 * step and the progress panel are today's, only where they appear changed.
 */
export const BulkLaunchRow = ({
  definitionId,
  requirementLabel,
  requirementKind,
  isMaterializing = false,
}: BulkLaunchRowProps) => {
  const isAdmin = useCan('manage_organization');
  const organizationId = useOrganizationId();
  const subject = useSubjectLanguageVariants();
  const [open, setOpen] = useState(false);
  /**
   * The stopped batch an admin chose "Send again" from (GDN-4149), or nothing.
   *
   * An id rather than a flag, so the choice is tied to the batch it was made
   * on: once a new batch exists — this admin's launch or anyone else's — the
   * latest batch is a different one and the modal goes back to showing it,
   * with no reset for anything to forget.
   */
  const [sendAgainFrom, setSendAgainFrom] = useState<string | undefined>();
  const launch = useBulkLaunch(organizationId, definitionId);
  const { patientRequirementsBulkLaunch } = useEventTracking();

  /*
    Not merely hidden: skipped. See the note above for the permission half, and
    the prop's own note for the materialization half — both are reasons the
    request must not be MADE, not reasons to drop its result.
  */
  const canPreview = isAdmin && Boolean(organizationId) && !isMaterializing;

  const {
    data: preview,
    isFetching,
    isError: previewFailed,
    refetch: refetchPreview,
  } = useGetBatchPreviewQuery(
    { orgId: organizationId ?? '', definitionId },
    {
      skip: !canPreview || !open,
      /**
       * Every open asks the server again, and that is not about freshness.
       *
       * The preview VERIFIES the population as it reads it — that is the AC-10
       * side effect, and it is attributed to the caller. Serving a reopen from
       * RTK Query's retained cache would show an audience that was verified
       * minutes ago and never re-verify it, so the admin would be reviewing a
       * claim the server is no longer making.
       */
      refetchOnMountOrArgChange: true,
    },
  );

  /**
   * The message is org-wide copy, not part of the batch preview — the audience
   * and the message are two different questions, answered by two endpoints.
   * Both are needed before a launch can be offered: AC-11 refuses to send
   * behind a message nobody has read.
   */
  const {
    data: copy,
    isFetching: copyIsFetching,
    isError: copyFailed,
    refetch: refetchCopy,
  } = useGetRequestCopyPreviewQuery(
    { orgId: organizationId ?? '' },
    { skip: !canPreview || !open },
  );

  /**
   * The batches this definition already has. Without it, reopening the row
   * after a launch shows a preview as though nothing had happened — and the
   * obvious next move for an admin looking at that is to launch again.
   */
  const {
    data: batches,
    isFetching: batchesIsFetching,
    isError: batchesFailed,
    refetch: refetchBatches,
  } = useGetBatchesQuery(
    { orgId: organizationId ?? '', definitionId, limit: 1 },
    { skip: !canPreview || !open, refetchOnMountOrArgChange: true },
  );

  const activeBatchId = launch.launchedBatchId ?? batches?.items?.[0]?.batch_id;

  /**
   * Held in state rather than derived inline, because the interval has to be
   * decided before the query that reports the state has answered. It starts
   * true so a batch we have not read yet is polled once; the effect below
   * turns it off the moment the server says the batch has stopped.
   */
  const [progressIsRunning, setProgressIsRunning] = useState(true);

  const {
    data: batchData,
    isError: batchFailed,
    refetch: refetchBatch,
  } = useGetBatchQuery(
    { orgId: organizationId ?? '', definitionId, batchId: activeBatchId ?? '' },
    {
      skip: !canPreview || !open || !activeBatchId,
      /**
       * Polls while the batch is working and stops when it is not. The
       * completed count keeps moving afterwards — the batch stops sending, the
       * patients do not stop completing — but a standing poll on a finished
       * batch would be a timer held open on a number the patients list reports
       * better. The panel says so instead, and re-reads when the row reopens.
       * PM ruling on AC-12.
       */
      pollingInterval: progressIsRunning ? PROGRESS_POLL_MS : 0,
      refetchOnMountOrArgChange: true,
    },
  );

  /**
   * Only the batch that was asked for. RTK Query keeps the previous result in
   * `data` while a new id loads, so right after a Send again the finished
   * batch would stand in for the new one — as a stopped batch, with Send again
   * live beside it, while the new send is going out. Until the new id has
   * answered this is "not loaded yet", which the loading gate already covers.
   */
  const batch = batchData?.batch_id === activeBatchId ? batchData : undefined;

  const cancelBatch = useBatchCancel(organizationId, definitionId, activeBatchId, () => {
    void refetchBatch();
  });

  // A new batch starts out polled, like the first one did. Redundant while its
  // first read succeeds (the id change fetches anyway); it is what retries
  // when that first read fails.
  useEffect(() => {
    setProgressIsRunning(true);
  }, [activeBatchId]);

  useEffect(() => {
    if (!batch) return;
    // One definition of running, shared with the panel and the mapper.
    setProgressIsRunning(isBatchRunning(batch.state));
  }, [batch]);

  if (!isAdmin) return null;

  /*
    While the panel evaluation runs there is no settled audience to review, so
    the row says so and offers nothing (GDN-3590).

    An honest state, not an error and not a partial list: the population is
    still being written, so anything shown mid-run would be a subset that reads
    as final — and a Send behind it would launch on that subset. Same rule as
    the rail card, which withholds its filter action while checking rather than
    disabling it.
  */
  if (isMaterializing) {
    return (
      <Stack
        direction="row"
        spacing={1}
        role="status"
        aria-live="polite"
        sx={{ alignItems: 'center', py: 0.5 }}
      >
        <CircularProgress size={14} />
        <Typography variant="caption" color="text.secondary">
          Checking existing records — who to send to is ready once this finishes.
        </Typography>
      </Stack>
    );
  }

  /**
   * The two failures are not the same failure, and collapsing them would lose
   * the more useful half.
   *
   * No audience means there is nothing to render at all — the panel is a
   * description of a population, so it needs a retry rather than a shape.
   * No COPY, with the audience in hand, is precisely what the panel's own
   * "the message could not be loaded" alert says, and it already withholds the
   * send behind it. Replacing that with a generic error would tell an admin
   * less than the panel would have.
   */
  const audienceFailed = previewFailed;

  /**
   * The two requests do not finish together, and an absent message reads on
   * the panel as a load failure. Rendering the moment the audience lands would
   * therefore show "the message could not be loaded" for the ordinary case of
   * one request being quicker than the other — so wait for copy, unless copy
   * is the thing that failed, in which case that alert is now true.
   */
  /**
   * Whether a batch already exists is part of "has this row finished loading",
   * not a detail that can settle afterwards (jotreviewer, #2967).
   *
   * The batch queries are two round trips behind the preview — `activeBatchId`
   * comes from the list, and the progress query cannot start until it does —
   * so on any open where preview and copy answer first, this component would
   * otherwise fall through to the preview branch and render a LIVE Send button
   * for a definition that is already being messaged. An admin clicking in that
   * window launches a second batch at the same population, which is the exact
   * failure the batch-replaces-preview rule exists to prevent.
   */
  /*
    Only while no batch is known. Once one is, the progress panel is the answer
    whatever the list is doing — and a launch or a stop refetches the list
    (its cache tag), which would otherwise swap the panel for a skeleton and
    drop the focused Stop button mid-dialog.
  */
  const batchExistenceUnknown =
    !batchesFailed && (batches === undefined || (batchesIsFetching && !activeBatchId));
  const activeBatchPending = Boolean(activeBatchId) && !batch && !batchFailed;

  /**
   * Whether a batch is already running is UNANSWERED if either query failed:
   * the list, or the progress read for an id the list did give us.
   *
   * Those two failures were handled in two places and answered differently
   * (jotreviewer, #2967 round 2) — `activeBatchPending` excuses `batchFailed`
   * from blocking, correctly, or a failed progress read would leave a
   * permanent skeleton; but the launch gate then never asked about it. So a
   * list that succeeded with a running batch, plus a progress read that
   * failed, left a live Send for a definition already being messaged. One
   * predicate now, used wherever the answer matters, because the asymmetry is
   * what produced the bug rather than either half being wrong.
   */
  const batchStateUnknown = batchesFailed || (Boolean(activeBatchId) && batchFailed);

  const stillLoading =
    isFetching ||
    copyIsFetching ||
    (Boolean(preview) && !copy && !copyFailed) ||
    batchExistenceUnknown ||
    activeBatchPending;

  const progress = batch ? toBatchProgressData(batch) : undefined;

  /**
   * Showing a fresh preview in place of a stopped batch (GDN-4149). Never for a
   * live one: the server allows one queued-or-running batch per requirement,
   * and a batch that is still sending keeps its panel and its Stop button.
   */
  const sendingAgain =
    progress !== undefined && sendAgainFrom === progress.batchId && isBatchStopped(progress.state);

  /**
   * Whether a new send may be offered at all. The list is not polled while a
   * stopped batch is shown, so a batch started elsewhere is only seen when the
   * list is re-read — on open and on Send again. While that re-read is out, the
   * latest batch is not known, and neither Send again nor the fresh preview's
   * Send is offered on the old answer.
   */
  const newSendUnknown = batchStateUnknown || batchesIsFetching;

  /**
   * A failed re-read keeps the last good copy in `data`. Shown with a live Send,
   * that is a message read earlier standing in for the one the server will
   * compose at launch (AC-11), so a failure means no copy.
   */
  const liveCopy = copyFailed ? undefined : copy;

  const data = preview
    ? toBulkLaunchPreviewData(
        preview,
        liveCopy
          ? {
              smsBody: liveCopy.sms.body,
              emailSubject: liveCopy.email.subject,
              emailBody: liveCopy.email.body,
            }
          : undefined,
      )
    : undefined;

  /** Every way out of the modal, reported once, with the audience if one loaded. */
  const dismiss = () => {
    setOpen(false);
    // Reopening starts from the latest batch in the list, not from a preview
    // and not from the batch this admin launched — a newer one may exist.
    setSendAgainFrom(undefined);
    launch.forgetLaunch();
    patientRequirementsBulkLaunch.closed({
      organization_id: organizationId ?? undefined,
      patient_requirement_definition_id: definitionId,
      kind: requirementKind,
      eligible_count: data?.eligiblePatients,
    });
  };

  // The label as well as the kind: the modal is detached from its row, and
  // two requirements can share a kind.
  const title = requirementKind
    ? `Send requests: ${requirementLabel} (${requirementKindLabel(requirementKind)})`
    : `Send requests: ${requirementLabel}`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <Button
        size="small"
        variant="outlined"
        onClick={() => {
          setOpen(true);
          // Ids, kinds and counts only — never the practice-authored label
          // or the message copy, and no patient_id: an admin acting on 128
          // people at once is one interaction, not 128.
          patientRequirementsBulkLaunch.opened({
            organization_id: organizationId ?? undefined,
            patient_requirement_definition_id: definitionId,
            kind: requirementKind,
          });
        }}
        sx={{ textTransform: 'none' }}
      >
        Send requests…
      </Button>

      <Dialog
        open={open}
        onClose={dismiss}
        fullWidth
        maxWidth="md"
        aria-labelledby={`bulk-launch-title-${definitionId}`}
      >
        <DialogTitle id={`bulk-launch-title-${definitionId}`}>{title}</DialogTitle>
        <DialogContent>
          {audienceFailed ? (
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    void refetchPreview();
                    if (copyFailed) void refetchCopy();
                  }}
                >
                  Try again
                </Button>
              }
            >
              The preview could not be loaded, so there is no audience to review.
            </Alert>
          ) : stillLoading ? (
            <Skeleton variant="rounded" height={220} />
          ) : progress && !sendingAgain ? (
            <>
              {cancelBatch.errorMessage && (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                  {cancelBatch.errorMessage}
                </Alert>
              )}
              {/*
              A batch exists, so the audience question is settled and the panel
              answers the next one instead. Showing the preview here would
              invite a second launch for a population already being messaged.
            */}
              <BatchProgressPanel
                requirementLabel={requirementLabel}
                state={progress.state}
                counts={progress.counts}
                completedPatients={progress.completedPatients}
                skippedPatients={progress.skippedPatients}
                skippedBreakdown={progress.skippedBreakdown}
                subject={subject}
                isCancelling={cancelBatch.isCancelling}
                onCancel={() => {
                  // Counts at the moment of the click, which is what says how much
                  // was stopped — after the cancel lands they describe the outcome
                  // instead, and the difference is the whole point of the event.
                  patientRequirementsBulkLaunch.stopClicked({
                    organization_id: organizationId ?? undefined,
                    patient_requirement_definition_id: definitionId,
                    batch_id: progress.batchId,
                    queued_count: progress.counts.queued,
                    sent_count: progress.counts.sent,
                  });
                  cancelBatch.cancel();
                }}
                /*
                  Withheld while the latest batch is not known: if the list
                  could not be read, or is being re-read, a newer batch may be
                  live.
                */
                onSendAgain={
                  newSendUnknown
                    ? undefined
                    : () => {
                        patientRequirementsBulkLaunch.sendAgainClicked({
                          organization_id: organizationId ?? undefined,
                          patient_requirement_definition_id: definitionId,
                          kind: requirementKind,
                          batch_id: progress.batchId,
                          batch_state: progress.state,
                        });
                        setSendAgainFrom(progress.batchId);
                        // Who would be messaged NOW: the preview this modal
                        // loaded on open is minutes old by this point, and the
                        // preview is the step that verifies the audience.
                        void refetchPreview();
                        void refetchCopy();
                        // And whether a batch has started elsewhere since the
                        // list was read on open.
                        void refetchBatches();
                      }
                }
              />
            </>
          ) : data ? (
            <BulkLaunchPreview
              requirementLabel={requirementLabel}
              requirementKind={requirementKind}
              eligiblePatients={data.eligiblePatients}
              alreadyCompletePatients={data.alreadyCompletePatients}
              excludedPatients={data.excludedPatients}
              messagesToSend={data.messagesToSend}
              exclusionBreakdown={data.exclusionBreakdown}
              excludedPatientsPage={data.excludedPatientsPage}
              messagePreview={data.messagePreview}
              canLaunch={data.canLaunch}
              subject={subject}
              /*
              Withheld whenever we could not find out whether a batch is
              already running. The audience is still worth reading, but
              offering a send on an unknown answer is the one thing here that
              cannot be taken back.
            */
              onLaunch={
                batchStateUnknown || (sendingAgain && batchesIsFetching)
                  ? undefined
                  : launch.openConfirm
              }
              onDismiss={dismiss}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={dismiss}>Close</Button>
        </DialogActions>
      </Dialog>

      {data && (
        <BulkLaunchConfirmDialog
          open={launch.isConfirming}
          requirementLabel={requirementLabel}
          eligiblePatients={data.eligiblePatients}
          messagesToSend={data.messagesToSend}
          subject={subject}
          isLaunching={launch.isLaunching}
          errorMessage={launch.errorMessage}
          onConfirm={() => {
            // Both counts ride the submit on purpose: a launch of 128 that
            // produces 217 delivery rows reads as a discrepancy when the two
            // numbers come from different places.
            patientRequirementsBulkLaunch.submitted({
              organization_id: organizationId ?? undefined,
              patient_requirement_definition_id: definitionId,
              kind: requirementKind,
              eligible_count: data.eligiblePatients,
              message_count: data.messagesToSend,
            });
            launch.confirm();
          }}
          onCancel={launch.closeConfirm}
        />
      )}
    </Box>
  );
};
